import React, { useContext, useMemo, useState } from 'react';

import { pieChart } from '@/assets/icons';
import { LayoutContext } from '@/components/Layout';
import convertSetlitToOpenSetlistConfig from '@/components/Layout/components/GenerateSetlist/helpers/convertSetlitToOpenSetlistConfig';
import { SetlistSummaryCard } from '@/components/SetlistSummaryCard';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { SmallTeamSongCard } from '@/components/SmallTeamSongCard';
import { TeamSongCard } from '@/components/TeamSongCard';
import { classNames } from '@/helpers/classNames';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { isPlatform } from '@ionic/react';
import { SvgIcon, useMediaQuery } from '@material-ui/core';

import { MessageCommonProps } from '../../RenderMessage';
import { MessageContent } from '../MessageContent';
import styles from './MessageSetlist.module.scss';

type MessageSetlistProps = MessageCommonProps & {};

export type SetlistMetadata = {
	setlistId: string;
	songsLength: number;
};

export const MessageSetlist = ({
	metadata,
	content,
	isAuthor,
	isFocused,
	setFocusedMessageId,
	id,
	chatId,
	isSending = false,
	isUserStatsOpen,
	className,
}: MessageSetlistProps) => {
	const { setlistId, songsLength: songsLengthInitial } = safeParse(metadata);
	const [songsLength, setSongsLength] = useState(songsLengthInitial);
	const { setlists, isLoading } = useSetlists();
	const { openSetlistWithCallback } = useLayout();

	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet})`);

	const setlist = useMemo(() => {
		const newSetlist = setlists.find((s) => s.id === setlistId);
		setSongsLength(newSetlist?.teamSongIds.length || 0);
		return newSetlist;
	}, [setlistId, setlists]);

	if (!setlist) {
		return (
			<MessageContent
				content="Setlist removed"
				{...{
					isAuthor,
					isFocused,
					setFocusedMessageId,
					id,
					chatId,
					isBot: false,
					isUserStatsOpen,
				}}
			/>
		);
	}

	return (
		<div
			className={classNames([
				styles['setlist'],
				isTablet && styles['hover'],
				className,
			])}
			onClick={() => {
				openSetlistWithCallback(() => {},
				convertSetlitToOpenSetlistConfig(setlist));
			}}
		>
			<SetlistSummaryCard
				isLoading={isLoading}
				size="md"
				setlistId={setlist.id}
				setlist={setlist}
				hasHover={false}
				negativeSpacing={false}
				timeClassName={styles['setlist-summary-time']}
				className={classNames([styles['setlist-summary-card']])}
			/>
			<div className={styles['songs']}>
				{Array.from(Array(songsLength)).map((v, i) => {
					const setlistTeamSongId = setlist.teamSongIds[i];

					return (
						<SmallTeamSongCard
							isLoading={isLoading}
							key={`setlist-song-${i}`}
							teamSongId={setlistTeamSongId}
							noClick
							displayKey
							className={styles['song']}
						/>
					);
				})}
			</div>
		</div>
	);
};

const safeParse = (metadata: string | null | undefined): SetlistMetadata => {
	try {
		if (!metadata) {
			throw new Error('Missing metadata');
		}
		return JSON.parse(metadata);
	} catch (e) {
		return { setlistId: '0', songsLength: 0 };
	}
};
